import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';

/**
 * Get the category name from a ticket, handling the typo in the API response
 * The API returns both 'maintenanceCatagoryName' (typo) and 'maintenanceCategoryName'
 * 
 * @param ticket - The maintenance ticket response
 * @param defaultValue - Default value if no category found (default: 'Maintenance')
 * @returns The category name
 */
export const getCategoryName = (
  ticket: IMaintenanceListResponse,
  defaultValue = 'Maintenance'
): string => {
  return ticket.maintenanceCatagoryName || ticket.maintenanceCategoryName || defaultValue;
};

/**
 * Get the subcategory name from a ticket
 * 
 * @param ticket - The maintenance ticket response
 * @param defaultValue - Default value if no subcategory found (default: '-')
 * @returns The subcategory name
 */
export const getSubCategoryName = (
  ticket: IMaintenanceListResponse,
  defaultValue = '-'
): string => {
  return (ticket as any).maintenanceSubCategoryName || defaultValue;
};

/**
 * Get the entry permission label (Yes/No/-) from a ticket
 * Handles various data types the API might return (boolean, number, undefined)
 * 
 * @param ticket - The maintenance ticket response
 * @returns 'Yes', 'No', or '-' if not set
 */
export const getEntryPermissionLabel = (ticket: IMaintenanceListResponse): string => {
  const permission = (ticket as any)?.permissionToEnter;
  
  if (typeof permission === 'undefined' || permission === null) {
    return '-';
  }
  
  // Handle boolean
  if (typeof permission === 'boolean') {
    return permission ? 'Yes' : 'No';
  }
  
  // Handle number (1 = yes, 0 = no)
  if (typeof permission === 'number') {
    return permission === 1 ? 'Yes' : 'No';
  }
  
  // Handle string
  if (typeof permission === 'string') {
    const lower = permission.toLowerCase();
    if (lower === 'yes' || lower === 'true' || lower === '1') {
      return 'Yes';
    }
    if (lower === 'no' || lower === 'false' || lower === '0') {
      return 'No';
    }
  }
  
  return '-';
};

/**
 * Get the problem description from a ticket
 * 
 * @param ticket - The maintenance ticket response
 * @param defaultValue - Default value if no problem found (default: '-')
 * @returns The problem description
 */
export const getProblemName = (
  ticket: IMaintenanceListResponse,
  defaultValue = '-'
): string => {
  return (ticket as any)?.maintenanceProblem || defaultValue;
};

/**
 * Get the location from a ticket
 * 
 * @param ticket - The maintenance ticket response
 * @param defaultValue - Default value if no location found (default: '-')
 * @returns The location name
 */
export const getLocationName = (
  ticket: IMaintenanceListResponse,
  defaultValue = '-'
): string => {
  return ticket.maintenanceLocation || defaultValue;
};

/**
 * Get the priority from a ticket
 * 
 * @param ticket - The maintenance ticket response
 * @param defaultValue - Default value if no priority found (default: '-')
 * @returns The priority level
 */
export const getPriorityName = (
  ticket: IMaintenanceListResponse,
  defaultValue = '-'
): string => {
  return ticket.maintenancePriority || defaultValue;
};

/**
 * Get a display title for a ticket
 * Uses the description truncated, or falls back to "Request #ID"
 * 
 * @param ticket - The maintenance ticket response
 * @param maxLength - Maximum length before truncation (default: 80)
 * @returns Display title for the ticket
 */
export const getTicketTitle = (
  ticket: IMaintenanceListResponse,
  maxLength = 80
): string => {
  const description = ticket.maintenanceDescription || '';
  
  if (description.length > maxLength) {
    return description.substring(0, maxLength - 3) + '...';
  }
  
  return description || `Request #${ticket.maintenanceRequestId}`;
};

/**
 * Get the ticket ID as a string
 * 
 * @param ticket - The maintenance ticket response
 * @returns The ticket ID as string
 */
export const getTicketId = (ticket: IMaintenanceListResponse): string => {
  return String(ticket.maintenanceRequestId || '');
};

/**
 * Get the best date to display for a ticket
 * Prefers createdOn, falls back to updatedOn
 * 
 * @param ticket - The maintenance ticket response
 * @returns The date string or undefined
 */
export const getDisplayDate = (ticket: IMaintenanceListResponse): string | undefined => {
  return ticket.createdOn || ticket.updatedOn;
};
